import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { FaBoxOpen, FaFilter, FaEye, FaTimesCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { FcDocument } from "react-icons/fc";
import Swal from "sweetalert2";


function AllOrders() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "All",
    dateFrom: null,
    dateTo: null,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  // 🟢 Fetch orders for vendor
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getOrdersByVendorId/${vendorId}`);
      if (res.data.success) {
        setOrders(res.data.orders || []);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error("Error fetching vendor orders:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId) fetchOrders();
  }, [vendorId]);

  // 🟢 Handle Cancel Order with Confirmation
const handleCancelOrder = async (orderUuid, orderId) => {
  const paddedId = String(orderId).padStart(10, '0');

  // 1. SweetAlert2 Confirmation Modal
  Swal.fire({
    title: "Cancel Order?",
    text: `Are you sure you want to cancel Order #${paddedId}? This will restock the inventory and log any required online refunds.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545", // Bootstrap Danger Red
    cancelButtonColor: "#6c757d",  // Bootstrap Secondary Gray
    confirmButtonText: "Yes, Cancel It",
    cancelButtonText: "No, Keep Order",
    reverseButtons: true, 
  }).then(async (result) => {
    
    if (result.isConfirmed) {
      try {
        setLoading(true); // Triggers visual table wrapper loaders

        // Match exactly what the new backend controller extracts from req.body
        const payload = {
          order_uuid: orderUuid,
          vendorId: vendorId // Extracted globally from localStorage
        };

        // 🟢 Console log data payload layout before flight
        console.log("✈️ Sending cancellation request to cancelOrderByVendor:", payload);

        // 2. POST or PUT request pointing to the explicit cancellation method endpoint
        // Change to .post() if your routing file uses router.post('/cancelOrderByVendor')
        const res = await axios.post(`${baseurl}cancelOrderByVendor`, payload);

        // 🟢 Console log the comprehensive backend response breakdown
        console.log("📥 Received execution response from server:", res.data);

        if (res.data.success) {
          // Customize text depending on if a refund entry was initiated
          const refundNotice = res.data.refund_logged 
            ? "\nAn online refund transaction has been initialized for the customer." 
            : "";

          // 3. Success Alert Modal
          Swal.fire({
            title: "Cancelled Successfully!",
            text: `Order #${paddedId} status is updated to Cancelled. Inventory inventory stocks have been restocked.${refundNotice}`,
            icon: "success",
            confirmButtonColor: "#2874f0"
          });
          
          // Re-fetch parent component dataset records
          fetchOrders();
        } else {
          // 4. API Logic handled failure message 
          Swal.fire({
            title: "Cancellation Rejected",
            text: res.data.message || "Failed to process cancellation rules.",
            icon: "error",
            confirmButtonColor: "#2874f0"
          });
        }
      } catch (err) {
        // 5. Hard Network/Database Failure Safety Catch Interceptor
        console.error("❌ Network execution runtime failure:", err);
        
        const backendErrorMessage = err.response?.data?.message || "An error occurred while communicating with the server.";
        
        Swal.fire({
          title: "Execution Error",
          text: backendErrorMessage,
          icon: "error",
          confirmButtonColor: "#2874f0"
        });
      } finally {
        setLoading(false); // Shuts down visual loading spinners
      }
    }
  });
};




  // 🟢 Filter logic
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesSearch =
        o.order_uuid.toLowerCase().includes(filters.search.toLowerCase()) ||
        (o.customer?.name || "").toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === "All" || o.order_status === filters.status;

      const orderDate = new Date(o.created_at);
      const matchesDate =
        (!filters.dateFrom || orderDate >= new Date(filters.dateFrom)) &&
        (!filters.dateTo || orderDate <= new Date(filters.dateTo));

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [orders, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setFilters({ ...filters, [name]: date[0] });
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> All Orders
            </h4>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={() => setShowFilter(true)}
            >
              <FaFilter /> Filters
            </button>
          </div>

          {/* Orders Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body table-responsive">
              {loading ? (
                <div className="text-center py-4 text-muted">Loading orders...</div>
              ) : (
                <table className="table table-hover table-striped align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length > 0 ? (
                      filteredOrders.map((o, i) => (
                        <tr key={o.order_uuid}>
                          <td>{i + 1}</td>
                          <td className="fw-bold">#{String(o.order_id).padStart(10, '0')}</td>
                          <td>
                            <div>
                              <div className="fw-semibold">{o.customer?.name}</div>
                              <small className="text-muted">{o.customer?.phone}</small>
                            </div>
                          </td>

                          {/* Show product images */}
                          <td>
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                              {o.items?.slice(0, 3).map((item, idx) => (
                                <img
                                  key={idx}
                                  src={
                                    item.product_image
                                      ? `${imgurl}${item.product_image}`
                                      : "/noimage.png"
                                  }
                                  alt={item.product_name}
                                  className="rounded shadow-sm border"
                                  style={{
                                    width: "38px",
                                    height: "38px",
                                    objectFit: "cover",
                                  }}
                                  title={`${item.product_name} ×${item.quantity}`}
                                />
                              ))}
                              {o.items?.length > 3 && (
                                <span className="small text-muted ms-1">
                                  +{o.items.length - 3} more
                                </span>
                              )}
                            </div>
                          </td>

                          <td>₹{o.vendor_total}</td>

                          <td>
                            <span
                              className={`badge ${
                                o.payment_status === "Paid"
                                  ? "bg-success"
                                  : "bg-secondary"
                              }`}
                            >
                              {o.payment_status}
                            </span>
                            <div>
                              <small className="text-muted">{o.payment_method}</small>
                            </div>
                          </td>

                          <td>
                            <span
                              className={`badge ${
                                o.order_status === "Pending"
                                  ? "bg-warning text-dark"
                                  : o.order_status === "Shipped"
                                  ? "bg-info text-dark"
                                  : o.order_status === "Delivered" ||
                                    o.order_status === "Confirmed"
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {o.order_status}
                            </span>
                          </td>

                          <td>
                            {new Date(o.created_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>

                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Link
                                to={`/vendor/order-details/${o.order_uuid}`}
                                className="btn btn-sm btn-outline-primary"
                                title="View Details"
                              >
                                <FaEye />
                              </Link>

                              <Link
                                to={`/vendor/invoice/${o.order_uuid}`}
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                title="Invoice"
                              >
                                <FcDocument /> <span className="d-none d-lg-inline">Invoice</span>
                              </Link>

                              {/* Only show cancel action if order is contextually active (Pending or Confirmed) */}
                              {(o.order_status === "Pending" || o.order_status === "Confirmed") && (
                                <button
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                  onClick={() => handleCancelOrder(o.order_uuid, o.order_id)}
                                  title="Cancel Order"
                                >
                                  <FaTimesCircle /> <span className="d-none d-lg-inline">Cancel</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* FILTER PANEL */}
          <div className={`filter-panel ${showFilter ? "open" : ""}`}>
            <div className="filter-header">
              <h5>Filters</h5>
              <button
                className="btn-close"
                onClick={() => setShowFilter(false)}
              ></button>
            </div>

            <div className="filter-body">
              <div className="mb-3">
                <label>Search</label>
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Order ID or Customer"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="mb-3">
                <label>Status</label>
                <select
                  className="form-control"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="All">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div className="mb-3">
                <label>Date From</label>
                <Flatpickr
                  value={filters.dateFrom}
                  onChange={(date) => handleDateChange("dateFrom", date)}
                  options={{ dateFormat: "Y-m-d" }}
                  className="form-control"
                  onOpen={(selectedDates, dateStr, instance) => {
                    // Quick fix for positioning issues inside fixed elements
                    instance.calendarContainer.style.zIndex = "1100";
                  }}
                />
              </div>

              <div className="mb-3">
                <label>Date To</label>
                <Flatpickr
                  value={filters.dateTo}
                  onChange={(date) => handleDateChange("dateTo", date)}
                  options={{ dateFormat: "Y-m-d" }}
                  className="form-control"
                  onOpen={(selectedDates, dateStr, instance) => {
                    instance.calendarContainer.style.zIndex = "1100";
                  }}
                />
              </div>

              <button
                className="btn btn-primary w-100"
                onClick={() => setShowFilter(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .filter-panel {
          position: fixed;
          right: -400px;
          top: 0;
          width: 350px;
          height: 100%;
          background: #fff;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
          transition: right 0.3s ease;
          z-index: 1050;
          padding: 20px;
        }
        .filter-panel.open {
          right: 0;
        }
        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ddd;
          margin-bottom: 15px;
        }
      `}</style>
    </>
  );
}

export default AllOrders;